import React from 'react'
import { cloneDeep } from 'lodash'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import 'dayjs/locale/ja'
import 'react-dates/initialize'
import { Button } from '@/components/atoms/button'
import { ReservationStatusType } from '@/models/reservation-reservation-status'
import { initializeSelectBoxStyle } from '@/constants/form'
import { DateRangeField } from '@/components/molecules/date-range-field'
import { EntireSearchContentType } from '@/models/entire-search'
import { Checkbox } from '@/components/atoms/checkbox'

type ContainerProps = {
  onClickReserch: () => void
  onClickClearReserch: () => void
  setIsDropDownReserchPanelOpen: (e?) => void
  detailedReserch: EntireSearchContentType
  setDetailedReserch: any
}

export const DetailedReserchPanel: React.FC<ContainerProps> = ({
  onClickReserch,
  onClickClearReserch,
  setIsDropDownReserchPanelOpen,
  detailedReserch,
  setDetailedReserch,
}) => {
  const { t } = useTranslation()
  // input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target
    setDetailedReserch({ ...detailedReserch, [name]: value })
  }

  // select change
  const handleSelectChange = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target
    setDetailedReserch(cloneDeep({ ...detailedReserch, [name]: value }))
  }

  // clear button
  const clearDetailedResearchPanelButton = () => {
    onClickClearReserch()
  }

  // search button
  const searchDetailedReserchPanelButton = () => {
    onClickReserch()
    setIsDropDownReserchPanelOpen()
  }

  const setReservationStatuses = value => {
    let adjustReservationStatuses = detailedReserch.reservationStatuses

    if (detailedReserch.reservationStatuses.includes(value)) {
      adjustReservationStatuses = detailedReserch.reservationStatuses.filter(reservationStatus => {
        return reservationStatus !== value
      })

      setDetailedReserch(
        cloneDeep({
          ...detailedReserch,
          reservationStatuses: adjustReservationStatuses,
        }),
      )
    } else {
      adjustReservationStatuses.push(value)

      setDetailedReserch(
        cloneDeep({
          ...detailedReserch,
          reservationStatuses: adjustReservationStatuses,
        }),
      )
    }
  }

  return (
    <div css={dropDownReserchPanelStyle}>
      <div css={topReserchPanelStyle}>
        <div>
          <div css={fieldTitleStyle}>{t('Full name')}</div>
          <input
            css={filedInputStyle}
            placeholder={t('John Doe')}
            name="name"
            value={detailedReserch.name}
            onChange={handleInputChange}
          ></input>
        </div>
        <div css={fieldBlockStyle}>
          <div css={fieldTitleStyle}>{t('Furigana')}</div>
          <input
            css={filedInputStyle}
            placeholder="ヤマダ　タロウ"
            name="nameKana"
            value={detailedReserch.nameKana}
            onChange={handleInputChange}
          ></input>
        </div>
        <div css={fieldBlockStyle}>
          <div css={fieldTitleStyle}>{t('Reservation ID')}</div>
          <input
            css={filedInputStyle}
            placeholder="12345678"
            name="searchReservationId"
            value={detailedReserch.searchReservationId}
            onChange={handleInputChange}
          ></input>
        </div>
        <div css={fieldBlockStyle}>
          <div css={fieldTitleStyle}>{t('Room number')}</div>
          <input
            css={filedInputStyle}
            placeholder="0101"
            name="roomNumber"
            value={detailedReserch.roomNumber}
            onChange={handleInputChange}
          ></input>
        </div>
      </div>
      <div css={middleReserchPanelStyle}>
        <div>
          <div css={fieldTitleStyle}>{t('Check-in date')}</div>
          <div css={checkinTimeStyle}>
            <DateRangeField
              beginDate={detailedReserch.checkinDateRange?.from}
              endDate={detailedReserch.checkinDateRange?.to}
              changeRangeDate={(from: string, to: string) => {
                setDetailedReserch({ ...detailedReserch, checkinDateRange: { from, to } })
              }}
            />
          </div>
        </div>
        <div css={{ marginLeft: 21 }}>
          <div css={fieldTitleStyle}>{t('Booking status')}</div>
          <div css={reservationStatusSelectStyle}>
            <Checkbox
              value={detailedReserch.reservationStatuses.includes(ReservationStatusType.NewReport)}
              onChange={() => setReservationStatuses(ReservationStatusType.NewReport)}
              label={t('New')}
              style={checkBoxOverrideStyle}
            />
            <Checkbox
              value={detailedReserch.reservationStatuses.includes(ReservationStatusType.ModificationReport)}
              onChange={() => setReservationStatuses(ReservationStatusType.ModificationReport)}
              label={t('Update')}
              style={checkBoxOverrideStyle}
            />
            <Checkbox
              value={detailedReserch.reservationStatuses.includes(ReservationStatusType.CancellationReport)}
              onChange={() => setReservationStatuses(ReservationStatusType.CancellationReport)}
              label={t('Cancel')}
              style={checkBoxOverrideStyle}
            />
            <Checkbox
              value={detailedReserch.reservationStatuses.includes(ReservationStatusType.NoshowReport)}
              onChange={() => setReservationStatuses(ReservationStatusType.NoshowReport)}
              label={t('No show')}
              style={checkBoxOverrideStyle}
            />
          </div>
        </div>
        <div css={{ position: 'relative', left: 22 }}>
          <div css={fieldTitleStyle}>{t('Room assignment status')}</div>
          <select
            name="selectGuestRoomAssign"
            css={assignSelectStyle}
            value={detailedReserch.selectGuestRoomAssign}
            onChange={handleSelectChange}
          >
            <option value="all">{t('All')}</option>
            <option value="assigned">{t('Assigned')}</option>
            <option value="notAssigned">{t('Not assigned')}</option>
          </select>
        </div>
      </div>
      <div css={bottomReserchPanelStyle}>
        <div css={assignButtonContainerStyle}>
          <Button width={92} height={32} buttonType={3} fontSize={12} onClick={clearDetailedResearchPanelButton}>
            {t('Clear')}
          </Button>
          <Button width={93} height={32} buttonType={1} fontSize={12} marginLeft={16} onClick={searchDetailedReserchPanelButton}>
            {t('Search')}
          </Button>
        </div>
      </div>
    </div>
  )
}

const dropDownReserchPanelStyle = css({
  width: '91.6%',
  paddingInline: 29,
  zIndex: 50,
  backgroundColor: '#FFF',
  position: 'absolute',
  top: 197,
  left: 72,
  boxShadow: '0px 3px 10px #0000003d',
  borderRadius: 5,
})

const topReserchPanelStyle = css({
  display: 'flex',
  marginTop: '32px',
  borderBottom: '1px solid rgb(242, 242, 242)',
  paddingBottom: '28px',
})

const middleReserchPanelStyle = css({
  display: 'flex',
  marginTop: '28px',
  borderBottom: '1px solid rgb(242, 242, 242)',
  paddingBottom: '28px',
})

const bottomReserchPanelStyle = css({
  display: 'flex',
  marginBlock: 16,
})

const fieldBlockStyle = css({
  width: '22.5%',
  maxWidth: 240,
  marginLeft: 21,
})

const fieldTitleStyle = css({
  fontSize: 12,
  fontWeight: 'bold',
  marginBottom: '12px',
})

const filedInputStyle = css({
  width: '100%',
  height: 32,
  padding: '8px 16px',
  border: '1px solid #CCCCCC',
  borderRadius: 17,
  textAlign: 'left',
  backgroundSize: 10,
  '::placeholder': {
    color: '#CCCCCC',
  },
})

const assignButtonContainerStyle = css({
  display: 'flex',
  justifyContent: 'space-between',
})

const reservationStatusSelectStyle = css({
  display: 'flex',
  height: 32,
  gap: 24,
})
const checkBoxOverrideStyle = css({ img: { width: 20, height: 20, p: { fontSize: 14 } } })

const assignSelectStyle = css(initializeSelectBoxStyle, {
  width: 240,
  height: 32,
  color: '#272727',
  border: '1px solid #CCCCCC',
  borderRadius: 19,
  paddingLeft: 16,
  backgroundImage: `url(${require('@/static/images/arrow_yellow.svg')})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: '95% center',
  backgroundSize: 10,
  display: 'block',
  marginBottom: 16,
})

const checkinTimeStyle = css({
  display: 'flex',
  alignItems: 'center',
  fontSize: 12,
  color: '#676767',
  fontWeight: 'bold',
  marginBottom: 23,
})
